import { useState } from "react";
import { type Column } from "@legal-500/legal500-design-system";
import {
  registrationGateListsService,
  type BlockedEmailDomain,
  type ProfanityAllowlistTerm,
} from "../../api/registrationGateLists.service";
import PermissionGate from "../../components/Permissions/PermissionGate";
import { UnauthorizedView } from "../../components/UnauthorizedView";
import { formatDateTimeShort } from "../../utils/dateFormatter";
import { RegistrationGateListSection } from "./RegistrationGateListSection";
import styles from "./RegistrationGate.module.css";

const BLOCKED_DOMAIN_COLUMNS: Column<BlockedEmailDomain>[] = [
  { header: "Domain", accessor: "domain" },
  {
    header: "Reason",
    accessor: "reason",
    render: (_value: unknown, row: BlockedEmailDomain) => row.reason ?? "—",
  },
  {
    header: "Added by",
    accessor: "createdBy",
    render: (_value: unknown, row: BlockedEmailDomain) => (
      <span className={styles.addedByCell}>{row.createdBy ?? "—"}</span>
    ),
  },
  {
    header: "Added",
    accessor: "createdAt",
    render: (_value: unknown, row: BlockedEmailDomain) => formatDateTimeShort(row.createdAt),
  },
];

const ALLOWLIST_COLUMNS: Column<ProfanityAllowlistTerm>[] = [
  { header: "Term", accessor: "term" },
  {
    header: "Reason",
    accessor: "reason",
    render: (_value: unknown, row: ProfanityAllowlistTerm) => row.reason ?? "—",
  },
  {
    header: "Added by",
    accessor: "createdBy",
    render: (_value: unknown, row: ProfanityAllowlistTerm) => (
      <span className={styles.addedByCell}>{row.createdBy ?? "—"}</span>
    ),
  },
  {
    header: "Added",
    accessor: "createdAt",
    render: (_value: unknown, row: ProfanityAllowlistTerm) => formatDateTimeShort(row.createdAt),
  },
];

type RegistrationGateTab = "blocklist" | "allowlist";

/**
 * Registration Gate admin page (Customer Services). The two lists differ in scope:
 * the blocklist is email-domain-only, while the profanity allow-list exempts terms
 * from the profanity check on both email domains and company names. Add and remove
 * are rendered by RegistrationGateListSection only for holders of
 * `staff.registration_gate.write`; read access is gated here.
 */
function RegistrationGatePage() {
  const [activeTab, setActiveTab] = useState<RegistrationGateTab>("blocklist");

  return (
    <PermissionGate permission="staff.registration_gate.read" fallback={<UnauthorizedView />}>
      <div className={styles.wrapper}>
        <div>
          <h1 className={styles.pageTitle}>Registration Gate</h1>
          <p className={styles.intro}>Changes take effect within ~1 minute.</p>
        </div>

        <div className={styles.tabBar} role="tablist" aria-label="Registration Gate lists">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "blocklist"}
            className={activeTab === "blocklist" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("blocklist")}
          >
            Blocked domains
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "allowlist"}
            className={activeTab === "allowlist" ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab("allowlist")}
          >
            Profanity allow-list
          </button>
        </div>

        {activeTab === "blocklist" && (
          <RegistrationGateListSection<BlockedEmailDomain>
            title="Blocked domains"
            description="Email domains blocked from registering."
            itemName="domains"
            searchPlaceholder="Search domains..."
            list={registrationGateListsService.listBlockedDomains}
            columns={BLOCKED_DOMAIN_COLUMNS}
            add={{
              kind: "blocklist",
              submit: ({ value, reason }) =>
                registrationGateListsService.addBlockedDomain({ domain: value, reason }),
              getValue: row => row.domain,
            }}
            remove={{
              kind: "blocklist",
              submit: (id, reason) => registrationGateListsService.removeBlockedDomain(id, reason),
              getValue: row => row.domain,
            }}
          />
        )}

        {activeTab === "allowlist" && (
          <RegistrationGateListSection<ProfanityAllowlistTerm>
            title="Profanity allow-list"
            description="Terms exempted from the profanity check on email domains and company names."
            itemName="terms"
            searchPlaceholder="Search terms..."
            list={registrationGateListsService.listAllowlistTerms}
            columns={ALLOWLIST_COLUMNS}
            add={{
              kind: "allowlist",
              submit: ({ value, reason }) =>
                registrationGateListsService.addAllowlistTerm({ term: value, reason }),
              getValue: row => row.term,
            }}
            remove={{
              kind: "allowlist",
              submit: (id, reason) => registrationGateListsService.removeAllowlistTerm(id, reason),
              getValue: row => row.term,
            }}
          />
        )}
      </div>
    </PermissionGate>
  );
}

export default RegistrationGatePage;
